import { Form, Formik, FormikProps } from "formik";
import { useEffect, useState } from "react";
import { exclamationIcon, timeLineIcon } from "../../assets/icons/SvgIconList";
import {
  ICFormikDropDown,
  ICFormikTextIArea,
  ICFormikTextInput,
} from "../../core-component/formik";
import { ICButton, Loader } from "../../core-component/ui";
import { IOption } from "../../data/AppInterface";
import UseEventCallbackHook from "../../hooks/UseEventCallbackHook";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { cn } from "../../lib";
import { prepareMessageFromParams } from "../../utils/AppFunctions";
import { formatPurchaseDateTime } from "../../utils/PurchaseFormat";
import { CHIP_CLASS, FOOTER_NOTE_MAX_LENGTH } from "../../utils/PurchaseConstants";
import BusinessInformationHistory from "./BusinessInformationHistory";
import {
  businessInformationValidationSchema,
  fetchBusinessInformation,
  fetchCountryOptions,
  fetchStateOptions,
  getBusinessInformationInitialValues,
  IBusinessInformationData,
  IStateOption,
  saveBusinessInformation,
  TBusinessInformationForm,
} from "./BusinessInformationController";

interface IBusinessInfoTextFieldProps {
  formikProps: FormikProps<TBusinessInformationForm>;
  fieldName: keyof TBusinessInformationForm;
  label: string;
  required?: boolean;
}

const BusinessInfoTextField: React.FC<IBusinessInfoTextFieldProps> = ({
  formikProps,
  fieldName,
  label,
  required = false,
}) => (
  <ICFormikTextInput
    {...formikProps}
    fieldName={fieldName}
    placeholder={label}
    required={required}
  />
);

const BusinessInformationView = () => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [data, setData] = useState<IBusinessInformationData>();
  const [countries, setCountries] = useState<IOption<string>[]>([]);
  const [states, setStates] = useState<IStateOption[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  const loadPage = UseEventCallbackHook(async () => {
    setLoading(true);
    await Promise.all([fetchBusinessInformation(setData), fetchCountryOptions(setCountries)]);
    setLoading(false);
  });

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const current = data?.current ?? null;
  const initialCountryId = data
    ? getBusinessInformationInitialValues(data, countries).country_id
    : "";

  useEffect(() => {
    fetchStateOptions(initialCountryId, setStates);
  }, [initialCountryId]);

  const handleSubmit = async (values: TBusinessInformationForm) => {
    const saved = await saveBusinessInformation(values, setSubmitLoading);
    if (saved) {
      await fetchBusinessInformation(setData);
    }
  };

  const header = (
    <div className="bg-subtle rounded-md p-4 flex flex-wrap items-center gap-3">
      <h4 className="font-semibold text-lg">{t("business_information")}</h4>
      {current ? (
        <>
          <span className={cn("px-2 rounded-full text-sm", CHIP_CLASS.info)}>
            {prepareMessageFromParams(t("business_info_version_current"), [
              ["version", String(current.version_no)],
            ])}
          </span>
          <span className="text-sm text-muted">
            {prepareMessageFromParams(t("business_info_saved_by"), [
              ["date", formatPurchaseDateTime(current.created_date)],
              ["name", current.saved_by ?? ""],
            ])}
          </span>
          <ICButton
            type="button"
            title={t("change_history")}
            className="ml-auto size-9 p-0 rounded-md bg-accent-muted text-accent grid place-items-center"
            onClick={() => setShowHistory(true)}
          >
            <span className="size-5">{timeLineIcon}</span>
          </ICButton>
        </>
      ) : (
        <span className={cn("px-2 rounded-full text-sm", CHIP_CLASS.danger)}>
          {t("business_info_no_version")}
        </span>
      )}
    </div>
  );

  if (loading) {
    return <Loader />;
  }
  if (!data) {
    return (
      <div className="flex flex-col items-center gap-3 p-8">
        <span className="text-muted">{t("business_info_load_failed")}</span>
        <ICButton type="button" className="w-fit" onClick={loadPage}>
          {t("try_again")}
        </ICButton>
      </div>
    );
  }

  return (
    <div className="w-full overflow-scroll scrollbar-remove p-4">
      <div className="space-y-4">
        {header}
        {!current && (
          <div className="rounded-md p-3 bg-danger-muted text-danger flex items-start gap-2">
            <span className="size-5 shrink-0">{exclamationIcon}</span>
            <div className="text-sm">
              <b>{t("purchases_switched_off")}</b> {t("purchases_switched_off_detail")}
            </div>
          </div>
        )}
        <Formik
          key={current?.id ?? "new"}
          initialValues={getBusinessInformationInitialValues(data, countries)}
          validationSchema={businessInformationValidationSchema(t, states)}
          onSubmit={handleSubmit}
        >
          {(formikProps) => (
            <Form
              onSubmit={formikProps.handleSubmit}
              className="bg-subtle rounded-md p-4 flex flex-col gap-4"
            >
              <fieldset className="pricing-sec">
                <legend className="font-semibold">{t("seller_details")}</legend>
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <BusinessInfoTextField
                      formikProps={formikProps}
                      fieldName="company_name"
                      label={t("company_name")}
                      required
                    />
                    <BusinessInfoTextField
                      formikProps={formikProps}
                      fieldName="gstin"
                      label={t("gstin")}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <BusinessInfoTextField
                      formikProps={formikProps}
                      fieldName="address_line1"
                      label={t("address_line_1")}
                      required
                    />
                    <BusinessInfoTextField
                      formikProps={formikProps}
                      fieldName="address_line2"
                      label={t("address_line_2")}
                    />
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <BusinessInfoTextField
                      formikProps={formikProps}
                      fieldName="city"
                      label={t("city")}
                      required
                    />
                    <ICFormikDropDown
                      {...formikProps}
                      name="state_id"
                      placeholder={t("state")}
                      options={states}
                      searchable
                      required
                    />
                    <ICFormikDropDown
                      {...formikProps}
                      name="country_id"
                      placeholder={t("country")}
                      options={countries}
                      searchable
                      required
                      onChange={(countryId: string) => {
                        formikProps.setFieldValue("state_id", "");
                        fetchStateOptions(countryId, setStates);
                      }}
                    />
                    <BusinessInfoTextField
                      formikProps={formikProps}
                      fieldName="zip_code"
                      label={t("zip_code")}
                      required
                    />
                  </div>
                </div>
              </fieldset>
              <fieldset className="pricing-sec">
                <legend className="font-semibold">{t("invoice_details")}</legend>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <BusinessInfoTextField
                    formikProps={formikProps}
                    fieldName="pan"
                    label={t("pan")}
                  />
                  <BusinessInfoTextField
                    formikProps={formikProps}
                    fieldName="email"
                    label={t("email")}
                  />
                  <BusinessInfoTextField
                    formikProps={formikProps}
                    fieldName="phone"
                    label={t("phone")}
                  />
                  <div>
                    <BusinessInfoTextField
                      formikProps={formikProps}
                      fieldName="sac_code"
                      label={t("sac_code")}
                    />
                    <div className="text-xs text-muted mt-1">{t("sac_code_hint")}</div>
                  </div>
                </div>
              </fieldset>
              <fieldset className="pricing-sec">
                <legend className="font-semibold">{t("document_footer_notes")}</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <ICFormikTextIArea
                      {...formikProps}
                      name="invoice_footer_note"
                      label={t("invoice_footer_note")}
                      maxLength={FOOTER_NOTE_MAX_LENGTH}
                    />
                    <div className="text-xs text-muted mt-1">{t("invoice_footer_note_hint")}</div>
                  </div>
                  <div>
                    <ICFormikTextIArea
                      {...formikProps}
                      name="receipt_footer_note"
                      label={t("receipt_footer_note")}
                      maxLength={FOOTER_NOTE_MAX_LENGTH}
                    />
                    <div className="text-xs text-muted mt-1">{t("receipt_footer_note_hint")}</div>
                  </div>
                </div>
              </fieldset>
              <div className="text-sm text-muted">
                {prepareMessageFromParams(t("business_info_next_version"), [
                  ["version", String((current?.version_no ?? 0) + 1)],
                ])}
              </div>
              <ICButton loading={submitLoading} className="w-full">
                {t("submit")}
              </ICButton>
            </Form>
          )}
        </Formik>
      </div>
      {showHistory && (
        <BusinessInformationHistory handleClose={() => setShowHistory(false)} />
      )}
    </div>
  );
};

export default BusinessInformationView;
